import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Combobox, Form, FormField, FormHelp, Icon, Label, VisuallyHidden } from '@zillow/constellation-10';
import { IconSearchFilled } from '@zillow/constellation-icons';
import { logError } from '@zg-rentals/log-error';
import type { RegionSuggestionsDto } from '@zg-rentals/ts-rental-growth-api-client';
import { useRentalGrowthRegionAutocompleteWebApiHandler_autocomplete } from '@zg-rentals/ts-rental-growth-api-client-react-query';
import debounce from 'lodash/debounce';

import type { ComboboxOptionType } from '@zillow/constellation-10';
import type { State } from 'app/shared/types/State';

const MIN_PARTIAL_SEARCH_LENGTH = 3;

export type AutocompleteAreaInputType = 'primary' | 'secondary';

interface AutocompleteAreaInputProps {
  submit: (region: RegionSuggestionsDto) => void;
  blockedAreas?: Array<string>;
  type?: AutocompleteAreaInputType;
  error?: string | null;
  placeholder?: string;
  label?: string;
  showLabel?: boolean;
}

const AutocompleteAreaInput: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<AutocompleteAreaInputProps>>
> = (props) => {
  const {
    submit,
    type = 'primary',
    error,
    blockedAreas = [],
    placeholder = 'Search by city or ZIP',
    label = 'City or ZIP',
    showLabel = false,
  } = props;
  const isMobile = useSelector((state: State) => state.app.device.isMobile);
  const [selectedIdx, setSelectedIdx] = useState<number>(-1);
  const [inputValue, setInputValue] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const updateSuggestions = useMemo(() => debounce(setSearchTerm, 200), []);
  useEffect(() => () => updateSuggestions.cancel(), [updateSuggestions]);
  const { data, error: suggestionsError } = useRentalGrowthRegionAutocompleteWebApiHandler_autocomplete(
    { partial: searchTerm },
    {
      enabled: searchTerm.length >= MIN_PARTIAL_SEARCH_LENGTH,
      throwOnError: false,
      meta: {
        tags: ['regionAutocomplete'],
        context: { api: 'rental-growth-api', details: 'Region autocomplete suggestions' },
      },
    },
  );
  const options = useMemo(
    () =>
      searchTerm.length >= MIN_PARTIAL_SEARCH_LENGTH
        ? (data?.regions ?? []).filter((region) => !blockedAreas.includes(region.resourceId))
        : [],
    [data, blockedAreas, searchTerm],
  );
  useEffect(() => {
    setSelectedIdx(options.length > 0 ? 0 : -1);
  }, [options]);
  useEffect(() => {
    if (suggestionsError) {
      logError({
        error: suggestionsError,
        errorType: 'AutocompleteAreaInput',
        context: {
          details: 'AutocompleteAreaInput: Error updating suggestions',
          root: 'components/AutocompleteAreaInput#updateSuggestions',
        },
      });
    }
  }, [suggestionsError]);
  const onSubmit = useCallback(
    (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();
      if (isOptionSelected) {
        submit(options[selectedIdx]);
      }
      e.stopPropagation(); // make sure listbox doesnt reappear
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    [submit, options, selectedIdx],
  );

  const isOptionSelected = selectedIdx !== -1;

  return (
    // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 485px` is past the end of the numeric sizes scale, which stops at `5000` (200px); `flexBasis: 485px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
    <Form onSubmit={onSubmit} css={{ maxWidth: '485px', flexBasis: '485px' }}>
      <FormField
        error={!!error}
        description={error ? <FormHelp>{error}</FormHelp> : undefined}
        label={
          showLabel ? (
            <Label>{label}</Label>
          ) : (
            <VisuallyHidden asChild>
              <Label>{label}</Label>
            </VisuallyHidden>
          )
        }
      >
        <Combobox
          css={{ width: '100%', backgroundColor: type === 'secondary' ? 'bg.neutral' : undefined }}
          autoCompleteBehavior="none"
          allowSubmitWhileOpen={true}
          focusFirstOption={true}
          placeholder={placeholder}
          options={options.map((region) => region.longName)}
          selectCallbackFrequency="always"
          onOptionSelect={(option: ComboboxOptionType) => {
            const val = typeof option === 'string' ? option : (option?.value ?? option);
            const idx = options.findIndex((m) => m.longName === val);
            if (idx !== -1) {
              submit(options[idx]);
            }
          }}
          size={isMobile ? 'sm' : 'md'}
          renderInput={({ ref: _ref, ...rest }) => (
            <Combobox.Input
              {...rest}
              value={inputValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.value;
                setInputValue(newValue);
                if (newValue.length >= MIN_PARTIAL_SEARCH_LENGTH) {
                  if (!isOptionSelected || (isOptionSelected && newValue !== options[selectedIdx].longName)) {
                    updateSuggestions(newValue);
                  }
                } else {
                  updateSuggestions.cancel();
                  setSearchTerm('');
                }
              }}
            />
          )}
          renderAdornment={({ ref: _ref, ...adornmentProps }) => (
            <Combobox.Adornment {...adornmentProps} id="search-adornment" onClick={onSubmit}>
              <Icon
                css={{
                  color: type === 'primary' ? 'icon.accent.brand.hero !important' : undefined,
                  cursor: 'pointer',
                }}
              >
                <IconSearchFilled />
              </Icon>
            </Combobox.Adornment>
          )}
        />
      </FormField>
    </Form>
  );
};

export default AutocompleteAreaInput;
